import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canOpenSection } from '../lib/permissions';
import { HeaderActions, KpiCard, VehicleThumb, LinkButton } from '../components/ui/dg';
import { DashboardActivity, DashboardData, DashboardDays, DashboardTodo, getDashboard } from '../api/services/dashboardService';

/**
 * Tableau de bord : ce qui se passe sur Dingo aujourd'hui, en un écran.
 *
 * Sur ordinateur (≥ 1024 px de large et ≥ 640 px de haut), la vue tient dans
 * la fenêtre sans défiler : les listes « À traiter » et « Activité récente »
 * affichent autant de lignes que la hauteur disponible en contient, le reste
 * est derrière « Voir tout ». Sur téléphone et tablette, les blocs
 * s'empilent et la page défile normalement — tout faire tenir à 360 px
 * rendrait chaque bloc illisible.
 *
 * Tout vient de GET /api/admin/dashboard (un appel).
 */

const PERIODS: { days: DashboardDays; label: string }[] = [
  { days: 7, label: '7 jours' },
  { days: 14, label: '14 jours' },
  { days: 30, label: '30 jours' }
];

// Couleur par type (fixe : elle suit le type, jamais son rang), dans l'ordre
// des segments de l'anneau — palette validée daltonisme, voisins compris.
// Les types rares sont regroupés en « Autres ».
const FLEET_SLOTS: { types: string[]; label: string; color: string }[] = [
  { types: ['car', 'van'], label: 'Voitures', color: '#4F46E5' },
  { types: ['scooter'], label: 'Scooters', color: '#8B85F5' },
  { types: ['trottinette', 'kickscooter'], label: 'Trottinettes', color: '#F29D16' },
  { types: ['bike'], label: 'Vélos', color: '#10B981' },
  { types: ['motorcycle'], label: 'Motos', color: '#C7C4F6' }
];
const OTHER_COLOR = '#9CA3AF';

const FIT_QUERY = '(min-width: 1024px) and (min-height: 640px)';
const nf = new Intl.NumberFormat('fr-FR');
const fmt = (n: number) => nf.format(Math.round(n || 0));

const photoUrl = (url: string | null | undefined) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`;
};

const ago = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const daysAgo = Math.round(hours / 24);
  if (daysAgo === 1) return 'hier';
  if (daysAgo < 30) return `il y a ${daysAgo} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const initials = (name: string | null) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

const AVATAR_TONES = ['bg-[#EEEDFD] text-[#4F46E5]', 'bg-[#F1F2F8] text-[#475569]', 'bg-[#FFF4DB] text-[#B45309]', 'bg-[#E7F7EF] text-[#047857]'];
const toneOf = (key: string) => AVATAR_TONES[key.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0) % AVATAR_TONES.length];

/** Mode « tient dans l'écran » : grand écran assez haut. */
function useFitMode() {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(FIT_QUERY).matches;
  const [fit, setFit] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(FIT_QUERY);
    const onChange = () => setFit(media.matches);
    onChange();
    // `change` ne part pas toujours (zoom, émulation d'écran) : resize en renfort.
    media.addEventListener('change', onChange);
    window.addEventListener('resize', onChange);
    return () => { media.removeEventListener('change', onChange); window.removeEventListener('resize', onChange); };
  }, []);
  return fit;
}

/** Combien de lignes de `rowHeight` px tiennent dans le conteneur. */
function useFitCount(fit: boolean, rowHeight: number, fallback: number): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(fallback);
  useLayoutEffect(() => {
    if (!fit) { setCount(fallback); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setCount(Math.max(1, Math.floor(el.clientHeight / rowHeight)));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fit, rowHeight, fallback]);
  return [ref, count];
}

// --- Icônes ---------------------------------------------------------------------------

const Svg = ({ children, className = 'h-[18px] w-[18px]' }: { children: React.ReactNode; className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">{children}</svg>
);
const IconLicense = () => <Svg><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6.5 15.5c.6-1.2 1.5-1.8 2.5-1.8s1.9.6 2.5 1.8M14 10h4M14 13h3" /></Svg>;
const IconIdentity = () => <Svg><circle cx="12" cy="8" r="3.5" /><path d="M5 20c1.2-3.5 3.8-5 7-5s5.8 1.5 7 5" /></Svg>;
const IconTicket = () => <Svg><path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8l-4 3v-3H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" /><path d="M8 9h8M8 12h5" /></Svg>;
const IconContest = () => <Svg><path d="M4 18c3-6 5 2 8-4s5 2 8-4" /><circle cx="4" cy="18" r="1.5" /><circle cx="20" cy="10" r="1.5" /></Svg>;
const IconCheck = () => <Svg><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.2l2.3 2.3 4.7-4.8" /></Svg>;

const TODO_META: Record<string, { tile: string; icon: React.ReactNode }> = {
  'verification:Vérification permis': { tile: 'bg-[#EEEDFD] text-[#4F46E5]', icon: <IconLicense /> },
  verification: { tile: 'bg-[#EEEDFD] text-[#4F46E5]', icon: <IconIdentity /> },
  ticket: { tile: 'bg-[#EEEDFD] text-[#4F46E5]', icon: <IconTicket /> },
  contest: { tile: 'bg-[#FDECEF] text-[#B91C1C]', icon: <IconContest /> }
};

const PRIORITY: Record<DashboardTodo['priority'], { label: string; tone: string }> = {
  urgent: { label: 'Urgent', tone: 'bg-red-50 text-red-700' },
  medium: { label: 'Moyen', tone: 'bg-amber-50 text-amber-800' },
  low: { label: 'Faible', tone: 'bg-gray-100 text-gray-600' }
};

const ACTIVITY_TAG: Record<DashboardActivity['kind'], { label: string; tone: string }> = {
  signup: { label: 'Inscription', tone: 'bg-gray-100 text-gray-700' },
  verification_submitted: { label: 'Vérification', tone: 'bg-indigo-50 text-indigo-700' },
  verification_approved: { label: 'Utilisateur', tone: 'bg-sky-50 text-sky-700' },
  rental_requested: { label: 'Trajet', tone: 'bg-violet-50 text-violet-700' },
  rental_completed: { label: 'Trajet', tone: 'bg-violet-50 text-violet-700' },
  vehicle_created: { label: 'Annonce', tone: 'bg-green-50 text-green-700' },
  ticket_opened: { label: 'Support', tone: 'bg-amber-50 text-amber-800' },
  ticket_resolved: { label: 'Support', tone: 'bg-sky-50 text-sky-700' }
};

const card = 'rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

/** « Voir tout » de l'activité : menu vers tous les trajets ou tous les comptes. */
const SeeAllMenu = ({ options }: { options: { label: string; hint: string; onSelect: () => void }[] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  if (options.length === 0) return null;
  if (options.length === 1) {
    return <button type="button" onClick={options[0].onSelect} className="text-[13px] font-bold text-[#4F46E5] hover:underline">Tout voir</button>;
  }
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[13px] font-bold text-[#4F46E5] hover:underline"
      >
        Tout voir
        <svg className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1.5 w-56 rounded-xl border border-gray-100 bg-white p-1 shadow-lg">
          {options.map((option) => (
            <button
              key={option.label}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); option.onSelect(); }}
              className="block w-full rounded-lg px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
            >
              <span className="block text-[13px] font-medium text-gray-900">{option.label}</span>
              <span className="block text-[11.5px] text-gray-500">{option.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Blocs -----------------------------------------------------------------------------

const Avatar = ({ item }: { item: DashboardActivity }) => {
  const [broken, setBroken] = useState(false);
  const isVehicle = item.kind === 'vehicle_created';
  const src = photoUrl(isVehicle ? item.vehicle_photo : item.photo);
  if (isVehicle) return <VehicleThumb vehicle={{ vehicle_photo: item.vehicle_photo, vehicle_type: item.vehicle_type }} size={32} radius={10} />;
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className="h-8 w-8 shrink-0 rounded-full object-cover" />;
  }
  if (item.kind === 'ticket_resolved') return <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E7F7EF] text-[#047857]"><IconCheck /></span>;
  return (
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11.5px] font-extrabold ${toneOf(item.name || item.kind)}`}>
      {initials(item.name)}
    </span>
  );
};

/**
 * Anneau de la flotte : un segment par type, 2 px d'espace entre segments.
 * Survol (ou toucher) d'un segment : infobulle type · nombre · part ; le
 * segment ressort, les autres s'estompent. `active` est partagé avec la
 * légende, dont le survol met aussi le segment en avant.
 */
type FleetSlice = { label: string; color: string; count: number };

const FleetDonut = ({ slices, total, active, onActive }: { slices: FleetSlice[]; total: number; active: string | null; onActive: (label: string | null) => void }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const r = 46;
  const circumference = 2 * Math.PI * r;
  const gap = slices.length > 1 ? 2.5 : 0;
  let offset = 0;
  const hovered = slices.find((s) => s.label === active) || null;

  const track = (label: string) => (event: React.PointerEvent<SVGCircleElement>) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setPointer({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    onActive(label);
  };

  return (
    <div ref={wrapRef} className="relative flex h-full w-full items-center justify-center" onPointerLeave={() => { onActive(null); setPointer(null); }}>
      <svg viewBox="0 0 120 120" className="h-full max-h-[150px] w-full max-w-[150px] overflow-visible" role="img" aria-label={`${total} véhicules en ligne : ${slices.map((s) => `${s.label} ${s.count}`).join(', ')}`}>
        <circle cx="60" cy="60" r={r} fill="none" stroke="#F1F2F8" strokeWidth="15" />
        {slices.map((slice) => {
          const length = total > 0 ? (slice.count / total) * circumference : 0;
          const dash = Math.max(length - gap, 0.5);
          const isActive = active === slice.label;
          const el = (
            <circle
              key={slice.label}
              cx="60" cy="60" r={r} fill="none" stroke={slice.color}
              strokeWidth={isActive ? 19 : 15}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 60 60)"
              opacity={active && !isActive ? 0.35 : 1}
              style={{ transition: 'opacity 120ms, stroke-width 120ms', cursor: 'pointer' }}
              onPointerEnter={track(slice.label)}
              onPointerMove={track(slice.label)}
              onPointerDown={track(slice.label)}
            />
          );
          offset += length;
          return el;
        })}
        <text x="60" y="58" textAnchor="middle" fontSize="26" fontWeight="800" fill="#1E293B" fontFamily="Figtree" style={{ pointerEvents: 'none' }}>
          {fmt(hovered ? hovered.count : total)}
        </text>
        <text x="60" y="75" textAnchor="middle" fontSize="11" fill="#6B7280" fontFamily="Figtree" style={{ pointerEvents: 'none' }}>
          {hovered ? hovered.label.toLowerCase() : total > 1 ? 'annonces' : 'annonce'}
        </text>
      </svg>
      {hovered && pointer && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-20 whitespace-nowrap rounded-lg border border-gray-100 bg-white px-2.5 py-1.5 text-[12px] shadow-lg"
          style={{ left: pointer.x, top: pointer.y, transform: 'translate(-50%, calc(-100% - 10px))' }}
        >
          <span className="flex items-center gap-1.5 font-medium text-gray-900">
            <span className="h-2 w-2 rounded-full" style={{ background: hovered.color }} />
            {hovered.label}
          </span>
          <span className="block tabular-nums text-gray-600">
            {fmt(hovered.count)} {hovered.count > 1 ? 'véhicules' : 'véhicule'} · {total > 0 ? Math.round((hovered.count / total) * 100) : 0} %
          </span>
        </div>
      )}
    </div>
  );
};

// --- Page -----------------------------------------------------------------------------

const Dashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = Number(params.get('periode'));
  const days: DashboardDays = PERIODS.some((p) => p.days === requested) ? (requested as DashboardDays) : 7;

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fit = useFitMode();
  const [activeSlice, setActiveSlice] = useState<string | null>(null);
  const [activeBar, setActiveBar] = useState<number | null>(null);
  const [todoRef, todoCount] = useFitCount(fit, 64, 5);
  const [activityRef, activityCount] = useFitCount(fit, 62, 6);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getDashboard(token, days));
    } catch (err: any) {
      setError(err.message || 'Tableau de bord indisponible.');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);
  // Les « il y a 12 min » vieillissent : on recalcule chaque minute, on recharge toutes les 5.
  const [, setTick] = useState(0);
  useEffect(() => {
    const tick = window.setInterval(() => setTick((n) => n + 1), 60000);
    const reload = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5 * 60000);
    return () => { window.clearInterval(tick); window.clearInterval(reload); };
  }, [load]);

  const selectPeriod = (value: DashboardDays) => {
    const next = new URLSearchParams(params);
    if (value === 7) next.delete('periode'); else next.set('periode', String(value));
    setParams(next, { replace: true });
  };

  const go = (section: string, path: string) => { if (canOpenSection(user?.role, section)) navigate(path); };
  const k = data?.kpis;
  const firstLoad = loading && !data;

  // KPI : valeur, écart à la veille, couleur, destination.
  const signed = (n: number) => (n > 0 ? `+${fmt(n)}` : n < 0 ? `−${fmt(Math.abs(n))}` : '±0');
  const kpis = [
    { key: 'pending', label: 'À traiter', value: k?.pending.count, delta: k ? `${signed(k.pending.new_24h)} depuis hier` : '', color: '#B45309', highlight: true, section: 'a-traiter', path: '/a-traiter' },
    { key: 'ongoing', label: 'Trajets en cours', value: k?.ongoing.now, delta: k ? `${signed(k.ongoing.now - k.ongoing.yesterday)} vs hier` : '', color: '#4F46E5', section: 'mytrips', path: '/mytrips' },
    { key: 'listings', label: 'Véhicules actifs', value: k?.listings.online, delta: k ? `${signed(k.listings.new_24h)} depuis hier` : '', color: '#047857', section: 'myrentals', path: '/myrentals' },
    { key: 'signups', label: 'Nouveaux utilisateurs', value: k?.signups.today, delta: k ? `${signed(k.signups.today - k.signups.yesterday)} vs hier` : '', color: '#4F46E5', section: 'users', path: '/users' }
  ];

  // Inscriptions : barres par jour, variation vs période précédente.
  const series = data?.signups.series || [];
  const peak = Math.max(1, ...series.map((d) => d.count));
  const signupChange = data && data.signups.prev_total > 0
    ? Math.round(((data.signups.total - data.signups.prev_total) / data.signups.prev_total) * 100)
    : null;
  const labelEvery = series.length <= 7 ? 1 : series.length <= 14 ? 2 : 5;

  // Flotte : créneaux fixes, puis « Autres ».
  const fleetCounts = new Map((data?.fleet || []).map((f) => [f.type, f.count]));
  const slices = FLEET_SLOTS.map((slot) => ({ label: slot.label, color: slot.color, count: slot.types.reduce((s, t) => s + (fleetCounts.get(t) || 0), 0) }));
  const known = new Set(FLEET_SLOTS.flatMap((s) => s.types));
  const others = (data?.fleet || []).filter((f) => !known.has(f.type)).reduce((s, f) => s + f.count, 0);
  if (others > 0) slices.push({ label: 'Autres', color: OTHER_COLOR, count: others });
  const fleetSlices = slices.filter((s) => s.count > 0);
  const fleetTotal = fleetSlices.reduce((s, f) => s + f.count, 0);

  const todo = data?.todo || [];
  const activity = data?.activity || [];
  const shownTodo = todo.slice(0, todoCount);
  const shownActivity = activity.slice(0, activityCount);

  const Skeleton = ({ className }: { className: string }) => <div className={`${className} animate-pulse rounded-xl bg-[#EEF0F5]`} />;

  return (
    <main className="dg-dash flex-1 overflow-y-auto bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
      {/* Grand écran assez haut : la vue tient dans la fenêtre, sans défiler. */}
      <style>{`@media ${FIT_QUERY}{.dg-dash{overflow:hidden}.dg-dash-inner{height:100%}.dg-dash-body{flex:1 1 0;min-height:0}}`}</style>
      <HeaderActions>
        <div className="flex gap-1.5">
          {PERIODS.map((p) => (
            <button key={p.days} type="button" onClick={() => selectPeriod(p.days)}
              className={`h-8 rounded-full border px-[13px] text-[12.5px] font-bold transition-colors ${
                p.days === days ? 'border-[#1E293B] bg-[#1E293B] text-white' : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-gray-50'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </HeaderActions>
      <div className="dg-dash-inner flex flex-col gap-4">
        {error && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#FDECEF] px-4 py-3 text-[13px] text-[#B91C1C]">
            <span>Le tableau de bord ne se charge pas. Réessayez dans un moment.</span>
            <LinkButton onClick={load}>Réessayer</LinkButton>
          </div>
        )}

        {/* Indicateurs */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
          {kpis.map((kpi) => (
            firstLoad ? (
              <div key={kpi.key} className={`${card} space-y-2 px-5 py-[18px]`}><Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-14" /><Skeleton className="h-3 w-20" /></div>
            ) : (
              <KpiCard
                key={kpi.key}
                label={kpi.label}
                value={fmt(kpi.value || 0)}
                sub={kpi.delta}
                color={kpi.color}
                highlight={kpi.highlight}
                onClick={canOpenSection(user?.role, kpi.section) ? () => go(kpi.section, kpi.path) : undefined}
              />
            )
          ))}
        </div>

        {/* À traiter, activité */}
        <div className="dg-dash-body grid min-h-0 grid-cols-1 gap-4 lg:grid-cols-[7fr_5fr] lg:grid-rows-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <section className={`${card} flex min-h-0 flex-col`}>
            <div className="flex items-center justify-between px-5 pb-1.5 pt-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">À traiter{data ? ` (${fmt(k?.pending.count || 0)})` : ''}</span>
              {canOpenSection(user?.role, 'a-traiter') && <LinkButton onClick={() => go('a-traiter', '/a-traiter')}>Tout voir</LinkButton>}
            </div>
            <div ref={todoRef} className="min-h-0 flex-1 overflow-hidden px-5">
              {firstLoad ? (
                <div className="space-y-3 py-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
              ) : todo.length === 0 ? (
                <p className="py-8 text-center text-[13px] text-[#6B7280]">Rien en attente. Tout est à jour.</p>
              ) : (
                <ul>
                  {shownTodo.map((item, i) => {
                    const meta = TODO_META[`${item.kind}:${item.title}`] || TODO_META[item.kind] || TODO_META.ticket;
                    const urgent = item.priority === 'urgent';
                    return (
                      <li key={`${item.kind}:${item.id}`} className={i > 0 ? 'border-t border-[#EEF0F4]' : ''}>
                        <button type="button" onClick={() => navigate(item.target)} className="flex h-16 w-full items-center gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]">
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${urgent ? 'bg-[#FDECEF] text-[#B91C1C]' : meta.tile}`}>{meta.icon}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[14px] font-semibold text-[#1E293B]">{item.title}</span>
                            <span className="block truncate text-[12px] text-[#6B7280]">{item.subtitle || PRIORITY[item.priority]?.label}</span>
                          </span>
                          <span className="shrink-0 text-[12px] text-[#9CA3AF]">{ago(item.created_at)}</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="h-2" />
          </section>

          <section className={`${card} flex min-h-0 flex-col`}>
            <div className="flex items-center justify-between px-5 pb-1.5 pt-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Activité récente</span>
              <SeeAllMenu
                options={[
                  { section: 'mytrips', label: 'Tous les trajets', hint: 'Recherche par nom, téléphone, véhicule…', path: '/mytrips' },
                  { section: 'users', label: 'Tous les utilisateurs', hint: 'Comptes, vérifications, suspensions', path: '/users' }
                ]
                  .filter((o) => canOpenSection(user?.role, o.section))
                  .map((o) => ({ label: o.label, hint: o.hint, onSelect: () => navigate(o.path) }))}
              />
            </div>
            <div ref={activityRef} className="min-h-0 flex-1 overflow-hidden px-5">
              {firstLoad ? (
                <div className="space-y-3 py-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : activity.length === 0 ? (
                <p className="py-8 text-center text-[13px] text-[#6B7280]">Pas encore d’activité.</p>
              ) : (
                <ul>
                  {shownActivity.map((item, i) => {
                    const title = item.kind === 'vehicle_created' || item.kind === 'ticket_resolved' ? item.name : item.name || 'Utilisateur';
                    return (
                      <li key={`${item.kind}:${item.at}:${i}`} className={i > 0 ? 'border-t border-[#EEF0F4]' : ''}>
                        <button type="button" onClick={() => navigate(item.target)} className="flex h-[62px] w-full items-center gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]">
                          <Avatar item={item} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13.5px] font-medium text-[#1E293B]">
                              <b className="font-semibold">{title}</b>{item.detail ? ` ${item.detail.charAt(0).toLowerCase()}${item.detail.slice(1)}` : ''}
                            </span>
                          </span>
                          <span className="shrink-0 text-[12px] text-[#9CA3AF]">{ago(item.at)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="h-2" />
          </section>

          {/* Inscriptions */}
          <section className={`${card} flex min-h-0 flex-col`}>
            <div className="flex items-center justify-between gap-2 px-5 pb-1.5 pt-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Inscriptions</span>
              {signupChange !== null && (
                <span
                  title={`vs les ${days} jours précédents`}
                  className={`rounded-lg px-2 py-[3px] text-[12px] font-bold ${signupChange >= 0 ? 'bg-[#E7F7EF] text-[#047857]' : 'bg-[#FDECEF] text-[#B91C1C]'}`}
                >
                  {signupChange > 0 ? '+' : ''}{signupChange} % vs la période d’avant
                </span>
              )}
            </div>
            {firstLoad ? (
              <div className="flex-1 px-5 pb-4"><Skeleton className="h-full min-h-[100px]" /></div>
            ) : (
              <div className="flex min-h-[150px] flex-1 flex-col px-6 pb-4 pt-2">
                <div className="relative flex min-h-0 flex-1 items-end justify-between gap-2.5" onPointerLeave={() => setActiveBar(null)}>
                  {series.map((d, i) => {
                    const isActive = activeBar === i;
                    const isToday = i === series.length - 1;
                    return (
                      <div
                        key={d.date}
                        className="relative flex h-full min-w-0 flex-1 cursor-pointer flex-col items-center justify-end gap-1.5"
                        onPointerEnter={() => setActiveBar(i)}
                        onPointerDown={() => setActiveBar(i)}
                        aria-label={`${new Date(`${d.date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} : ${d.count} ${d.count > 1 ? 'inscriptions' : 'inscription'}`}
                      >
                        {series.length <= 14 && (
                          <span className={`text-[11px] font-bold ${isToday || isActive ? 'text-[#4F46E5]' : 'text-[#9CA3AF]'}`}>{d.count}</span>
                        )}
                        <div
                          className="w-full max-w-[38px] rounded-[8px_8px_4px_4px] transition-colors duration-100"
                          style={{
                            height: `${Math.max((d.count / peak) * 82, d.count > 0 ? 6 : 2)}%`,
                            background: isActive || isToday ? '#4F46E5' : '#C7C4F6'
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1.5 flex justify-between gap-2.5">
                  {series.map((d, i) => (
                    <span key={d.date} className="min-w-0 flex-1 text-center text-[11px] text-[#6B7280]">
                      {(series.length - 1 - i) % labelEvery === 0
                        ? (series.length <= 7
                          ? new Date(`${d.date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short' }).replace(/^./, (c) => c.toUpperCase())
                          : new Date(`${d.date}T12:00:00`).getDate())
                        : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Flotte */}
          <section className={`${card} flex min-h-0 flex-col`}>
            <div className="px-5 pb-1.5 pt-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Répartition flotte</span>
            </div>
            {firstLoad ? (
              <div className="flex-1 px-5 pb-4"><Skeleton className="h-full min-h-[120px]" /></div>
            ) : fleetTotal === 0 ? (
              <p className="py-8 text-center text-[13px] text-[#6B7280]">Aucune annonce en ligne.</p>
            ) : (
              <div className="flex min-h-0 flex-1 items-center gap-6 px-6 pb-3.5">
                <div className="flex h-full min-h-[120px] w-[160px] shrink-0 items-center justify-center">
                  <FleetDonut slices={fleetSlices} total={fleetTotal} active={activeSlice} onActive={setActiveSlice} />
                </div>
                <ul className="min-w-0 flex-1">
                  {fleetSlices.map((s) => (
                    <li
                      key={s.label}
                      onMouseEnter={() => setActiveSlice(s.label)}
                      onMouseLeave={() => setActiveSlice(null)}
                      className={`flex cursor-default items-center gap-2 py-1.5 text-[13px] transition-opacity ${activeSlice && activeSlice !== s.label ? 'opacity-50' : ''}`}
                    >
                      <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: s.color }} />
                      <span className="min-w-0 flex-1 truncate text-[#1E293B]">{s.label}</span>
                      <b className="tabular-nums text-[#1E293B]">{fmt(s.count)}</b>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
